import type { ViewComponent } from "@frui.ts/views/dist";
import { observer } from "mobx-react-lite";
import AlertWithIcon from "views/common/alertWithIcon";
import Col from "react-bootstrap/Col";
import React from "react";
import Form from "react-bootstrap/Form";
import RefundChargeViewModel from "viewModels/callCenter/search/charges/refundChargeViewModel";
import FuelTypeChangeViewModel from "viewModels/callCenter/search/charges/fuelTypeChangeRefundViewModel";
import { formatAmount, translateEnum } from "utils/localizations";
import classNames from "classnames";
import RefundPaymentType from "entities/refundPaymentType";
import { formatIBAN } from "@edaz/utils/bankAccount";
import CustomerPartialRefundCandidatePreviewDetailResponse from "entities/customerPartialRefundCandidatePreviewDetailResponse";
import CustomerRefundCandidatePreviewDetailResponse from "entities/customerRefundCandidatePreviewDetailResponse";

// eslint-disable-next-line sonarjs/cognitive-complexity
export const RefundPreview: ViewComponent<RefundChargeViewModel | FuelTypeChangeViewModel> = observer(({ vm }) => {
  if (vm instanceof RefundChargeViewModel && !vm.previewPartialResponse) {
    return null;
  }
  if (vm instanceof FuelTypeChangeViewModel && !vm.previewPartialResponse && !vm.previewFuelTypeResponse) {
    return null;
  }
  const previewResponse = vm.previewPartialResponse?.candidatePreview;
  const refundPayment = vm.previewPartialResponse?.candidatePreview.refundPayment;

  const refundAmountRows = (
    previewResponse: CustomerRefundCandidatePreviewDetailResponse | CustomerPartialRefundCandidatePreviewDetailResponse
  ) => {
    return (
      <>
        <tr className={classNames("final")}>
          <td className="header">{vm.translatePreview("refundAmount")}:</td>
          <td className="value">{formatAmount(previewResponse.refundAmount)}</td>
        </tr>
      </>
    );
  };

  const foreignTransferPanel = refundPayment?.transferPayment &&
    previewResponse?.refundPayment &&
    (previewResponse?.refundPayment.paymentType === RefundPaymentType.ForeignTransfer ||
      previewResponse?.refundPayment.paymentType === RefundPaymentType.SepaTransfer) && (
      <div className="bg-light mb-4 px-4 pt-4 rounded">
        <Form.Row>
          {vm.previewPartialResponse?.officialRecordId && (
            <Form.Group as={Col} sm={12} xs={12}>
              <Form.Label>{vm.translatePreview("officialRecordId")}</Form.Label>
              <div>{vm.previewPartialResponse.officialRecordId}</div>
            </Form.Group>
          )}
          {refundPayment.paymentType && (
            <Form.Group as={Col} sm={6} xs={12}>
              <Form.Label>{vm.translatePreview("paymentType")}</Form.Label>
              <div>{translateEnum("refundPaymentType", refundPayment.paymentType)}</div>
            </Form.Group>
          )}
          {refundPayment.paymentType === RefundPaymentType.SepaTransfer &&
          refundPayment.transferPayment.counterpartyBank.bankAccount.accountNumber ? (
            <>
              <Form.Group as={Col} sm={6} xs={12}></Form.Group>
              <Form.Group as={Col} sm={6} xs={12}>
                <Form.Label>{vm.translateGeneral("finance.iban")}</Form.Label>
                <div>{formatIBAN(refundPayment.transferPayment.counterpartyBank.bankAccount.accountNumber)}</div>
              </Form.Group>
            </>
          ) : (
            <>
              {refundPayment.transferPayment.counterpartyBank.codeType && (
                <Form.Group as={Col} sm={6} xs={12}>
                  <Form.Label>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "codeType")}</Form.Label>
                  <div>{refundPayment.transferPayment.counterpartyBank.codeType}</div>
                </Form.Group>
              )}
              {refundPayment.transferPayment.counterpartyBank.bankAccount.accountNumber && (
                <Form.Group as={Col} sm={6} xs={12}>
                  <Form.Label>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "accountNumber")}</Form.Label>
                  <div>{refundPayment.transferPayment.counterpartyBank.bankAccount.accountNumber}</div>
                </Form.Group>
              )}
            </>
          )}
          {refundPayment.transferPayment.counterpartyBank.bankAccount.bank && (
            <Form.Group as={Col} sm={6} xs={12}>
              <Form.Label>{vm.translateGeneral("swift")}</Form.Label>
              <div>{refundPayment.transferPayment.counterpartyBank.bankAccount.bank}</div>
            </Form.Group>
          )}
        </Form.Row>
        {refundPayment.paymentType === RefundPaymentType.ForeignTransfer && (
          <Form.Row>
            {refundPayment.transferPayment.counterpartyBank.name && (
              <Form.Group as={Col} sm={12} xs={12}>
                <Form.Label>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "name")}</Form.Label>
                <div>{refundPayment.transferPayment.counterpartyBank.name}</div>
              </Form.Group>
            )}
            {refundPayment.transferPayment.counterpartyBank.cityAddress && (
              <Form.Group as={Col} sm={6} xs={12}>
                <Form.Label>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "cityAddress")}</Form.Label>
                <div>{refundPayment.transferPayment.counterpartyBank.cityAddress}</div>
              </Form.Group>
            )}
            {refundPayment.transferPayment.counterpartyBank.countryId && (
              <Form.Group as={Col} sm={6} xs={12}>
                <Form.Label>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "countryId")}</Form.Label>
                <div>{vm.translateCountryName(refundPayment.transferPayment.counterpartyBank.countryId)}</div>
              </Form.Group>
            )}
          </Form.Row>
        )}
        <hr className="mt-0"></hr>
        <Form.Row>
          {refundPayment.transferPayment.counterpartyName && (
            <Form.Group as={Col} sm={6} xs={12}>
              <Form.Label>{vm.translateAttribute("transferRefundPayment", "counterpartyName")}</Form.Label>
              <div>{refundPayment.transferPayment.counterpartyName}</div>
            </Form.Group>
          )}
          {refundPayment.transferPayment.counterpartyCountryId && (
            <Form.Group as={Col} sm={6} xs={12}>
              <Form.Label>{vm.translateAttribute("transferRefundPayment", "counterpartyCountryCode")}</Form.Label>
              <div>{vm.translateCountryName(refundPayment.transferPayment.counterpartyCountryId)}</div>
            </Form.Group>
          )}
          {refundPayment.transferPayment.counterpartyStreetAddress && (
            <Form.Group as={Col} sm={6} xs={12}>
              <Form.Label>{vm.translateAttribute("transferRefundPayment", "counterpartyStreetAddress")}</Form.Label>
              <div>{refundPayment.transferPayment.counterpartyStreetAddress}</div>
            </Form.Group>
          )}
          {refundPayment.transferPayment.counterpartyCityAddress && (
            <Form.Group as={Col} sm={6} xs={12}>
              <Form.Label>{vm.translateAttribute("transferRefundPayment", "counterpartyCityAddress")}</Form.Label>
              <div>{refundPayment.transferPayment.counterpartyCityAddress}</div>
            </Form.Group>
          )}
        </Form.Row>
        <Form.Row>
          {refundPayment.transferPayment.additionalAvMessage && (
            <Form.Group as={Col} sm={6} xs={12}>
              <Form.Label>{vm.translateAttribute("transferRefundPayment", "additionalAvMessage")}</Form.Label>
              <div>{refundPayment.transferPayment.additionalAvMessage}</div>
            </Form.Group>
          )}
        </Form.Row>
      </div>
    );

  return (
    <>
      <div className="mb-4">
        {refundPayment?.isTransferredToSepa && (
          <AlertWithIcon variant="warning">{vm.translatePreview("transferredToSepa")}</AlertWithIcon>
        )}
        {vm instanceof FuelTypeChangeViewModel && vm.rejectedReasonText && (
          <AlertWithIcon variant="danger">{vm.rejectedReasonText}</AlertWithIcon>
        )}
        {vm instanceof FuelTypeChangeViewModel && vm.informationText && !vm.rejectedReasonText && (
          <AlertWithIcon variant="info">{vm.informationText}</AlertWithIcon>
        )}
        {vm instanceof RefundChargeViewModel && vm.informationText && !vm.rejectedReason && (
          <AlertWithIcon variant="info">{vm.informationText}</AlertWithIcon>
        )}
        {vm instanceof RefundChargeViewModel && vm.rejectedReason && (
          <AlertWithIcon variant="danger">{vm.translatePreview(`rejectedReason.${vm.rejectedReason}`)}</AlertWithIcon>
        )}
        {vm instanceof FuelTypeChangeViewModel &&
          vm.warningReasons?.map(reason => (
            <AlertWithIcon key={reason} variant="warning">
              {vm.translatePreview(`warningReason.${reason}`)}
            </AlertWithIcon>
          ))}
      </div>
      {foreignTransferPanel}
      {(previewResponse instanceof CustomerRefundCandidatePreviewDetailResponse ||
        previewResponse instanceof CustomerPartialRefundCandidatePreviewDetailResponse) && (
        <table className="my-4 w-100 alternate-colors refund-preview">
          <tbody>
            <tr>
              <td className="header">{vm.translatePreview("chargePrice")}:</td>
              <td className="value">{formatAmount(previewResponse?.chargePrice)}</td>
            </tr>
            {vm.newChargePrice && (
              <tr>
                <td className="header">{vm.translatePreview("newChargePrice")}:</td>
                <td className="value">{formatAmount(vm.newChargePrice)}</td>
              </tr>
            )}
            {previewResponse?.fees.map(x => (
              <tr key={x.feeType}>
                <td className="header">{translateEnum("feeType", x.feeType)}:</td>
                <td className="value">{formatAmount(-x.amount)}</td>
              </tr>
            ))}
            {previewResponse?.refundPayment && refundAmountRows(previewResponse)}
          </tbody>
        </table>
      )}
    </>
  );
});

export default RefundPreview;
